import { MonetaryAmount } from "../../../currency/currencyTypes"
import { OrderLineType } from "../../../../shared/components/createOrder/createOrderTypes"
import type { ExtraServiceActionType } from "../../admin/extraServices/extraServicesTypes"
import { OrderLine, OrderStatus } from "../../controlKitchenTypes"
import { MeetingServicePaymentType, PaymentMethodType } from "../../../payments/paymentTypes"
import { ResourceType } from "../../../serviceCenter/admin/organization/resources/resourcesTypes"
import { OrderRecurrence } from "../../../nonAdminUser/reports/home/weekMenuTypes"
import { OrderSummary } from "../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { DeliveryType, OrderStatusDetails } from "../../merchant/production/meetingService/meetingServiceTypes"
import { Organizer } from "../../../../shared/types/OrganizerTypes"
import { BillReceiversType } from "../../../../shared/components/billReceivers/billReceiverTypes"
import { ChatMessage } from "../../../../shared/components/orderChat/orderChatTypes"
import { DynamicConfigKey } from "../../../../shared/components/createOrder/components/form/formTypes"

type AccountingCustomer = Readonly<{
    type: "customer"
    id: number
    name: string
    isActive: boolean
    hasSales: boolean
}>

export type AccountingCustomerGroup = Readonly<{
    type: "customerGroup"
    id: number
    customerId: number
    name: string
    isActive: boolean
    hasSales: boolean
}>

export type AccountingDebtor = AccountingCustomer | AccountingCustomerGroup

type Product = Readonly<{
    id: number
    name: string
    code: string
    numberOfItems: number
    totalAmount: MonetaryAmount
}>

type Account = Readonly<{
    name: string
    amount: MonetaryAmount
    products?: Product[]
}>

type AccountingCategory = Readonly<{
    name: string
    accounts: Account[]
    amount: MonetaryAmount
}>

export type SalesByAccount = Readonly<{
    categories: AccountingCategory[]
}>

export type DebtorWithSalesByAccount = AccountingDebtor &
    Readonly<{
        sales: SalesByAccount
    }>

export type AccountingDetailsCostCodes = Readonly<{
    dimensionId: number
    dimensionName: string
    name?: string
    label: string
    value: string
}>

export type MeetingServiceOrderResource = Readonly<{
    id: number
    name: string
    resourceTypeId: number
    resourceType: ResourceType
    serviceCenterId: number
    room: {
        tableSetup: {
            key: string
            name: string
            imageUrl: string
        }
    }
}>

export enum ReferenceType {
    ORIGINAL_ORDER = "ORIGINAL_ORDER",
    CREDIT = "CREDIT",
    DEBIT = "DEBIT",
}

export type MeetingServiceOrder = Readonly<{
    serviceResponse?: {
        displayMessage: string
        details: string
    }
    id: number
    uid: string
    lineNumber: number
    webshop: {
        id: number
        name?: string
    }
    merchant: {
        id: number
        name: string
    }
    priceList: {
        id: number
        name?: string
        includeVat: boolean
    }
    orderSummary: OrderSummary
    orderStatus?: OrderStatus
    orderLines?: OrderLine[]
    amount: MonetaryAmount
    orderType: string
    meetingDetails: MeetingServiceOrderMeetingDetails
    accountingDetails: {
        bookedTime: string
        accountingCostCode: string
        costCodes: AccountingDetailsCostCodes[]
        isBooked: boolean
        references?: AccountingDetailsReference[]
    }
    deliveryDetails: MeetingServiceOrderDeliveryDetails
    deliveries: {
        deliveryTime: string
        deliveryType: string // eg. "PICK_UP"
        id: number
        orderLines: (
            | {
                  id: number
                  type: OrderLineType.EXTRA_SERVICE
                  action?: ExtraServiceActionType
                  productId: number
                  buyerParty: string
                  deliveryTime?: string
                  name: string
                  items: number
                  itemPrice: MonetaryAmount
                  price: MonetaryAmount
                  isAdminOnly?: boolean
              }
            | {
                  id?: number
                  type: DynamicConfigKey.PRODUCT_WISH
                  buyerParty?: string
                  deliveryTime?: string
                  itemPrice: MonetaryAmount
                  items: number
                  name: string
                  price: MonetaryAmount
                  productId: number
              }
            | {
                  id: number
                  type?: undefined
                  buyerParty: string
                  deliveryTime?: string
                  itemPrice: MonetaryAmount
                  items: number
                  name: string
                  price: MonetaryAmount
                  productId: number
                  priceListId?: number
                  isAdminOnly?: boolean
              }
        )[]
        orderNote: string
        price: MonetaryAmount
    }[]
    customer: {
        id: number
        email: string
        displayName: string
        givenName: string
        surname: string
        initials: string
        employeeId: string
        department: {
            name: string
            number: string
        }
        userGroup: {
            id: number
            name: string
        }
    }
    buyerCustomerParty: {
        id: number
        type?: BillReceiversType
        uid: string
        name: string
        phoneNumber: string
        city: string
        email: string
        address?: {
            displayName?: string
        }
        contactDetails?: {
            name?: string
            email?: string
            phoneNumber?: string
        }
        streetName: string
        postalCode: string
        streetNumber: string
        debtorNo?: string
        debtorId?: string
        ean: string
        vatnumber: string
    }
    paymentDetails: {
        method: PaymentMethodType
        salesChannel: string
        type: MeetingServicePaymentType
        isContantPayment: boolean
    }
    orderNote: string
    accountingCustomerParty?: {
        uid: string
        name: string
        phoneNumber: string
        email: string
        streetName: string
        streetNumber: string
        postalCode: string
        city: string
        contactPerson: string
        vatNumber: string
    }
    sellerSupplierParty: {
        uid: string
        name: string
        phoneNumber: string
        email: string
        streetName: string
        streetNumber: string
        postalCode: string
        city: string
        contactPerson: string
        caseNumber: string
    }
    organizers: Organizer[]
    bookingDetails: {
        resources: MeetingServiceOrderResource[]
    }
    messageToServiceCenter?: string
    created?: string
    updated?: string
    messages?: ChatMessage[]
}>

type MeetingServiceOrderLocationAsObject = Readonly<{
    id?: number
    name: string
}>

export type MeetingServiceOrderMeetingDetails = Readonly<{
    meetingName: string
    location?: string
    locationAsObject?: MeetingServiceOrderLocationAsObject
    recurrence?: OrderRecurrence
    // Outlook calendar item id when the order was created from an Outlook meeting. Signals that
    // meeting details (incl. the recurrence rule) are managed in Outlook.
    calendarAppointmentId?: string
    startDateTime: string
    endDateTime: string
    attendees?: {
        company: string
        name: string
        email: string
        isExternal: boolean
    }[]
    organizers?: Organizer[]
    isPrivate?: boolean
    isExternal?: boolean
    serviceCenterNote?: string
}>

export type MeetingServiceOrderDeliveryDetails = Readonly<{
    isLocked: boolean
    isConfirmed: boolean
    time: string
    location: MeetingServiceOrderLocationAsObject
    type: DeliveryType | string
    sittings: number
    hasChanged: boolean
    statusDetails: OrderStatusDetails
    satellite?: SatelliteItem
}>

export type AccountingDetailsReference = Readonly<{
    amount: MonetaryAmount
    bookedTime: string
    orderId: number
    referenceType: ReferenceType
}>

type SatelliteItem = Readonly<{
    id: number
    name: string
}>
